import React, { useState, useEffect } from 'react';
import { Select } from 'chakra-react-select';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Spacer,
  Input,
  NativeSelect,
  Textarea,
  Checkbox,
  Button,
  SimpleGrid,
  Card,
  Separator,
  Icon,
  Badge,
  Flex,
  Accordion,
  Alert,
  IconButton,
  Dialog,
  Spinner,
  Tabs
} from '@chakra-ui/react';
import { FiTrash2, FiUpload, FiDownload, FiFileText, FiCheckCircle, FiTrash, FiEdit2, FiCamera } from 'react-icons/fi';
import { FaPlus } from 'react-icons/fa';
import { LuContainer, LuPackage, LuPlane } from 'react-icons/lu';
import axios from 'axios';
import { dataService } from '../services/dataService';
import { branchDataService } from '../services/branchDataService';
import specialServiceOptionsService from '../services/specialServiceOptionsService';
import countryService from '../services/countryService';
import { downloadExampleExcel, downloadExampleCSV } from '../utils/exportUtils';
import { config } from '../config';
import { useAuth } from '../context/AuthContext';
import { extractFromImages, getBatches, deleteBatch } from '../services/aiVisionService';

const API_URL = config.BACKEND_URL;


const INITIAL_FORM_DATA = {
  country: '',
  agent_name: '',
  short_name: '',
  fcl: 'NO',
  lcl: 'NO',
  air: 'NO',
  inland: 'NO',
  pic_overseas: '',
  email_pic: '',
  phone_number: '',
  manager: '',
  email_manager: '',
  phone_manager: '',
  mbl_information: '',
  status: 'APPROVED',
  contract: 'NO',
  additional: '',
  network: '',
  city_port: '',
  class: '',
  special_service: '',
  strongest_service: '',
  remark: '',
  membership_validity: '',
  suspended: false,
  suspended_remark: '',
  consol_export: false,
  consol_import: false,
  consol_oversea: false
};

const CreateAgent = () => {
  /* const toast = useToast(); */
  const toast = (options) => console.log('Toast:', options);
  const bgColor = 'gray.50';
  const cardBg = 'white';

  const { canEditMain, canEditBranch, hasRole } = useAuth();

  // Tab state for Main/Branch
  const [activeTab, setActiveTab] = useState(() => {
    if (canEditMain()) return 'main';
    if (canEditBranch()) return 'branch';
    return 'main';
  });

  const [formData, setFormData] = useState(() => ({ ...INITIAL_FORM_DATA }));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('');
  const [specialServiceOptions, setSpecialServiceOptions] = useState([]);
  const [countries, setCountries] = useState([]);

  // Special Service Options Management
  const [showOptionsManager, setShowOptionsManager] = useState(false);
  const [newOption, setNewOption] = useState({ option_name: '', description: '' });
  const [editingOption, setEditingOption] = useState(null);
  const [optionsMessage, setOptionsMessage] = useState(null);
  const [optionsMessageType, setOptionsMessageType] = useState('');
  const [showInactiveOptions, setShowInactiveOptions] = useState(false);

  // Country Management
  const [showCountryManager, setShowCountryManager] = useState(false);
  const [newCountry, setNewCountry] = useState({ name: '', code: '' });
  const [editingCountry, setEditingCountry] = useState(null);
  const [countryMessage, setCountryMessage] = useState(null);
  const [countryMessageType, setCountryMessageType] = useState('');
  const [showInactiveCountries, setShowInactiveCountries] = useState(false);

  // Tab change handler
  const handleTabChange = (details) => {
    const newTab = details.value;
    setActiveTab(newTab);
    // Reset form and messages when switching tabs
    setFormData({ ...INITIAL_FORM_DATA });
    setMessage(null);
    setUploadFile(null);
    setUploadMessage(null);
    setSelectedCityPorts([]);
  };

  // File Upload State
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [uploadMessageType, setUploadMessageType] = useState('');

  // City Ports State
  const [cityPorts, setCityPorts] = useState([]);
  const [selectedCityPorts, setSelectedCityPorts] = useState([]);
  const [showAddCityPort, setShowAddCityPort] = useState(false);
  const [newCityPortData, setNewCityPortData] = useState({
    name: ''
  });
  const [newCityPortPics, setNewCityPortPics] = useState([
    { pic_name: '', pic_email: '', pic_phone: '' }
  ]);
  const [addingCityPort, setAddingCityPort] = useState(false);

  // State for PIC management of selected city ports
  const [cityPortPicsMap, setCityPortPicsMap] = useState({}); // { cityPortId: [pics] }
  const [editingPic, setEditingPic] = useState(null); // { cityPortId, picId, pic_name, pic_email, pic_phone }
  const [addingPicToCityPort, setAddingPicToCityPort] = useState(null); // cityPortId
  const [newPicData, setNewPicData] = useState({ pic_name: '', pic_email: '', pic_phone: '' });
  const [savingPic, setSavingPic] = useState(false);

  // AI Vision Import State
  const [showVisionImport, setShowVisionImport] = useState(false);
  const [visionFiles, setVisionFiles] = useState([]);
  const [visionProcessing, setVisionProcessing] = useState(false);
  const [visionError, setVisionError] = useState(null);
  const [fillingFields, setFillingFields] = useState({});
  const [savedBatches, setSavedBatches] = useState([]);
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const visionFileInputRef = React.useRef(null);

  // Load saved vision batches on mount
  useEffect(() => {
    const loadBatches = async () => {
      try {
        setLoadingBatches(true);
        const data = await getBatches();
        setSavedBatches(data.batches || []);
      } catch (err) {
        console.error('Failed to load vision batches:', err);
      } finally {
        setLoadingBatches(false);
      }
    };
    loadBatches();
  }, []);

  // AI Vision - handle file selection
  const handleVisionFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 5) {
      setVisionError('Maximum 5 images allowed at once');
      return;
    }
    setVisionFiles(files);
    setVisionError(null);
  };

  // AI Vision - process images
  const handleVisionExtract = async () => {
    if (visionFiles.length === 0) return;
    setVisionProcessing(true);
    setVisionError(null);
    setFillingFields({});

    try {
      const data = await extractFromImages(visionFiles);
      const results = data.results || [];

      // Refresh saved batches (auto-saved by backend)
      try {
        const batchData = await getBatches();
        setSavedBatches(batchData.batches || []);
      } catch (e) {
        console.error('Failed to refresh batches:', e);
      }

      // Auto-fill form with first successful result
      const firstSuccess = results.find(r => r.data && !r.error);
      if (firstSuccess && firstSuccess.data) {
        setActiveBatchId(firstSuccess.batchId || null);
        await animateFillForm(firstSuccess.data);
      } else if (results.every(r => r.error)) {
        const errors = results.map(r => r.filename + ': ' + r.error).join('; ');
        setVisionError('Extraction failed: ' + errors);
      }
    } catch (err) {
      setVisionError(err.response?.data?.error || err.message || 'Failed to process images');
    } finally {
      setVisionProcessing(false);
    }
  };

  // Animate filling form fields one by one
  const animateFillForm = async (data) => {
    const fieldMapping = [
      ['agent_name', data.agent_name],
      ['short_name', data.short_name],
      ['country', data.country],
      ['city_port', data.city_port],
      ['pic_overseas', data.pic_overseas],
      ['email_pic', data.email_pic],
      ['phone_number', data.phone_number],
      ['manager', data.manager],
      ['email_manager', data.email_manager],
      ['phone_manager', data.phone_manager],
      ['fcl', data.fcl],
      ['lcl', data.lcl],
      ['air', data.air],
      ['inland', data.inland],
      ['network', data.network],
      ['special_service', data.special_service],
      ['remark', data.remark],
      ['strongest_service', data.strongest_service],
      ['mbl_information', data.mbl_information],
    ];

    const confidence = data.confidence || {};

    for (const [field, value] of fieldMapping) {
      if (value && value !== '' && value !== 'NO') {
        setFillingFields(prev => ({ ...prev, [field]: { filling: true, confidence: confidence[field] } }));
        setFormData(prev => ({ ...prev, [field]: value }));
        await new Promise(r => setTimeout(r, 150));
        setFillingFields(prev => ({ ...prev, [field]: { filling: false, confidence: confidence[field] } }));
      }
    }
  };

  // Apply a specific vision result to the form
  const applyVisionResult = async (resultData, batchId = null) => {
    setFormData({ ...INITIAL_FORM_DATA });
    setFillingFields({});
    setActiveBatchId(batchId);
    await animateFillForm(resultData);
  };

  // Get field style based on AI confidence
  const getFieldVisionStyle = (fieldName) => {
    const info = fillingFields[fieldName];
    if (!info) return {};
    if (info.filling) return { borderColor: 'blue.400', bg: 'blue.50', transition: 'all 0.3s' };
    if (info.confidence !== undefined && info.confidence < 0.7) {
      return { borderColor: 'yellow.400', bg: 'yellow.50' };
    }
    if (info.confidence !== undefined && info.confidence >= 0.7) {
      return { borderColor: 'green.400', bg: 'green.50', transition: 'all 0.5s' };
    }
    return {};
  };

  useEffect(() => {
    if (!canEditMain() && canEditBranch()) {
      setActiveTab('branch');
    }
  }, [canEditMain, canEditBranch]);

  useEffect(() => {
    loadSpecialServiceOptions();
  }, [showInactiveOptions]);

  useEffect(() => {
    loadCountries();
  }, [showInactiveCountries]);

  const loadSpecialServiceOptions = async () => {
    try {
      const activeOnly = !showInactiveOptions;
      const options = await specialServiceOptionsService.getAllOptions(activeOnly);
      setSpecialServiceOptions(options);
    } catch (error) {
      console.error('Error loading special service options:', error);
    }
  };

  const loadCountries = async () => {
    try {
      const activeOnly = !showInactiveCountries;
      const result = await countryService.getAllCountries(activeOnly);
      setCountries(result);
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  // Load city ports
  useEffect(() => {
    loadCityPorts();
  }, []);

  const loadCityPorts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/city-ports`, {
        withCredentials: true
      });
      setCityPorts(response.data);
    } catch (error) {
      console.error('Error loading city ports:', error);
    }
  };

  // Load PICs for selected city ports
  const loadPicsForCityPorts = async (cityPortIds) => {
    if (!cityPortIds || cityPortIds.length === 0) {
      setCityPortPicsMap({});
      return;
    }
    const picsMap = {};
    for (const cpId of cityPortIds) {
      try {
        const response = await axios.get(`${API_URL}/api/city-ports/${cpId}/pics`, {
          withCredentials: true
        });
        picsMap[cpId] = response.data;
      } catch (error) {
        console.error(`Error loading PICs for city port ${cpId}:`, error);
        picsMap[cpId] = [];
      }
    }
    setCityPortPicsMap(picsMap);
  };

  // Handle edit PIC
  const handleEditPic = (cityPortId, pic) => {
    setEditingPic({
      cityPortId,
      picId: pic.id,
      pic_name: pic.pic_name || '',
      pic_email: pic.pic_email || '',
      pic_phone: pic.pic_phone || ''
    });
  };

  // Save edited PIC
  const handleSavePic = async () => {
    if (!editingPic || !editingPic.pic_name.trim()) return;
    setSavingPic(true);
    try {
      await axios.put(`${API_URL}/api/city-ports/pics/${editingPic.picId}`, {
        pic_name: editingPic.pic_name,
        pic_email: editingPic.pic_email,
        pic_phone: editingPic.pic_phone
      }, { withCredentials: true });

      // Refresh PICs for this city port
      const response = await axios.get(`${API_URL}/api/city-ports/${editingPic.cityPortId}/pics`, {
        withCredentials: true
      });
      setCityPortPicsMap(prev => ({ ...prev, [editingPic.cityPortId]: response.data }));
      setEditingPic(null);
    } catch (error) {
      console.error('Error saving PIC:', error);
      setMessage(error.response?.data?.error || 'Failed to save PIC');
      setMessageType('error');
    } finally {
      setSavingPic(false);
    }
  };

  // Add new PIC to existing city port
  const handleAddPicToExisting = async (cityPortId) => {
    if (!newPicData.pic_name.trim()) return;
    setSavingPic(true);
    try {
      await axios.post(`${API_URL}/api/city-ports/${cityPortId}/pics`, newPicData, {
        withCredentials: true
      });

      // Refresh PICs for this city port
      const response = await axios.get(`${API_URL}/api/city-ports/${cityPortId}/pics`, {
        withCredentials: true
      });
      setCityPortPicsMap(prev => ({ ...prev, [cityPortId]: response.data }));
      setAddingPicToCityPort(null);
      setNewPicData({ pic_name: '', pic_email: '', pic_phone: '' });
    } catch (error) {
      console.error('Error adding PIC:', error);
      setMessage(error.response?.data?.error || 'Failed to add PIC');
      setMessageType('error');
    } finally {
      setSavingPic(false);
    }
  };

  // Delete PIC
  const handleDeletePic = async (picId, cityPortId) => {
    if (!window.confirm('Are you sure you want to delete this PIC?')) return;
    try {
      await axios.delete(`${API_URL}/api/city-ports/pics/${picId}`, {
        withCredentials: true
      });

      // Refresh PICs for this city port
      const response = await axios.get(`${API_URL}/api/city-ports/${cityPortId}/pics`, {
        withCredentials: true
      });
      setCityPortPicsMap(prev => ({ ...prev, [cityPortId]: response.data }));
    } catch (error) {
      console.error('Error deleting PIC:', error);
      setMessage(error.response?.data?.error || 'Failed to delete PIC');
      setMessageType('error');
    }
  };

  const handleAddCityPortInline = async () => {
    if (!newCityPortData.name.trim()) return;
    setAddingCityPort(true);
    try {
      // Filter out empty PICs, but ensure at least one if desired (optional)
      const picsToSend = newCityPortPics
        .filter(p => p.pic_name && p.pic_name.trim())
        .map(p => ({
          name: p.pic_name,
          email: p.pic_email,
          phone: p.pic_phone
        }));

      const payload = {
        name: newCityPortData.name,
        pics: picsToSend
      };

      const response = await axios.post(`${API_URL}/api/city-ports`, payload, {
        withCredentials: true
      });
      const createdCityPort = response.data.cityPort;
      // Add to city ports list
      setCityPorts(prev => [...prev, createdCityPort].sort((a, b) => a.name.localeCompare(b.name)));
      // Auto-select the new city port
      setSelectedCityPorts(prev => [...prev, createdCityPort.id]);
      // Reset form
      setNewCityPortData({ name: '' });
      setNewCityPortPics([{ pic_name: '', pic_email: '', pic_phone: '' }]);
      setShowAddCityPort(false);
    } catch (error) {
      console.error('Error adding city port:', error);
      // You might want to show this error in UI
    } finally {
      setAddingCityPort(false);
    }
  };


  // --- Special Service Options Management Functions ---
  const handleAddOption = async (e) => {
    e.preventDefault();
    if (!newOption.option_name || newOption.option_name.trim() === '') {
      setOptionsMessage('Option name is required.');
      setOptionsMessageType('error');
      return;
    }

    try {
      await specialServiceOptionsService.createOption(newOption);
      setOptionsMessage('Special service option added successfully.');
      setOptionsMessageType('success');
      setNewOption({ option_name: '', description: '' });
      loadSpecialServiceOptions();
    } catch (error) {
      console.error('Error adding option:', error);
      setOptionsMessage(error.response?.data?.error || 'Failed to add option');
      setOptionsMessageType('error');
    }
  };

  const handleUpdateOption = async (e) => {
    e.preventDefault();
    if (!editingOption.option_name || editingOption.option_name.trim() === '') {
      setOptionsMessage('Option name is required.');
      setOptionsMessageType('error');
      return;
    }

    try {
      await specialServiceOptionsService.updateOption(editingOption.id, editingOption);
      setOptionsMessage('Special service option updated successfully.');
      setOptionsMessageType('success');
      setEditingOption(null);
      loadSpecialServiceOptions();
    } catch (error) {
      console.error('Error updating option:', error);
      setOptionsMessage(error.response?.data?.error || 'Failed to update option');
      setOptionsMessageType('error');
    }
  };

  const handleDeleteOption = async (id, optionName) => {
    if (!window.confirm(`Are you sure you want to delete "${optionName}"?`)) {
      return;
    }

    try {
      const result = await specialServiceOptionsService.deleteOption(id);
      setOptionsMessage(result.message);
      setOptionsMessageType('success');
      loadSpecialServiceOptions();
    } catch (error) {
      console.error('Error deleting option:', error);
      setOptionsMessage(error.response?.data?.error || 'Failed to delete option');
      setOptionsMessageType('error');
    }
  };

  const handleToggleActiveOption = async (id) => {
    try {
      const result = await specialServiceOptionsService.toggleActiveStatus(id);
      setOptionsMessage(result.message);
      setOptionsMessageType('success');
      loadSpecialServiceOptions();
    } catch (error) {
      console.error('Error toggling option status:', error);
      setOptionsMessage(error.response?.data?.error || 'Failed to toggle option status');
      setOptionsMessageType('error');
    }
  };

  const startEditingOption = (option) => {
    setEditingOption({ ...option });
    setOptionsMessage(null);
  };

  const cancelEditingOption = () => {
    setEditingOption(null);
    setOptionsMessage(null);
  };

  // --- Country Management Functions ---
  const handleAddCountry = async (e) => {
    e.preventDefault();
    if (!newCountry.name || newCountry.name.trim() === '') {
      setCountryMessage('Country name is required.');
      setCountryMessageType('error');
      return;
    }

    try {
      await countryService.createCountry(newCountry);
      setCountryMessage('Country added successfully.');
      setCountryMessageType('success');
      setNewCountry({ name: '', code: '' });
      loadCountries();
    } catch (error) {
      console.error('Error adding country:', error);
      setCountryMessage(error.response?.data?.error || 'Failed to add country');
      setCountryMessageType('error');
    }
  };

  const handleUpdateCountry = async (e) => {
    e.preventDefault();
    if (!editingCountry.name || editingCountry.name.trim() === '') {
      setCountryMessage('Country name is required.');
      setCountryMessageType('error');
      return;
    }

    try {
      await countryService.updateCountry(editingCountry.id, editingCountry);
      setCountryMessage('Country updated successfully.');
      setCountryMessageType('success');
      setEditingCountry(null);
      loadCountries();
    } catch (error) {
      console.error('Error updating country:', error);
      setCountryMessage(error.response?.data?.error || 'Failed to update country');
      setCountryMessageType('error');
    }
  };

  const handleDeleteCountry = async (id, countryName) => {
    if (!window.confirm(`Are you sure you want to delete "${countryName}"?`)) {
      return;
    }

    try {
      const result = await countryService.deleteCountry(id);
      setCountryMessage(result.message);
      setCountryMessageType('success');
      loadCountries();
    } catch (error) {
      console.error('Error deleting country:', error);
      setCountryMessage(error.response?.data?.error || 'Failed to delete country');
      setCountryMessageType('error');
    }
  };

  const handleToggleActiveCountry = async (id) => {
    try {
      const result = await countryService.toggleActiveStatus(id);
      setCountryMessage(result.message);
      setCountryMessageType('success');
      loadCountries();
    } catch (error) {
      console.error('Error toggling country status:', error);
      setCountryMessage(error.response?.data?.error || 'Failed to toggle country status');
      setCountryMessageType('error');
    }
  };

  const startEditingCountry = (country) => {
    setEditingCountry({ ...country });
    setCountryMessage(null);
  };

  const cancelEditingCountry = () => {
    setEditingCountry(null);
    setCountryMessage(null);
  };

  // --- Form Handling ---

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'suspended_remark') {
        return { ...prev, [name]: value, suspended: true };
      }
      return {
        ...prev,
        [name]: value
      };
    });
  };

  const handleCountryChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      country: selectedOption ? selectedOption.value : ''
    }));
  };

  // Helper to check if a special service is selected
  const isSpecialServiceSelected = (serviceName) => {
    if (!formData.special_service) return false;
    const services = formData.special_service.split(';').map(s => s.trim()).filter(s => s);
    return services.includes(serviceName);
  };

  // Helper to check if a strongest service is selected
  const isServiceSelected = (serviceName) => {
    if (!formData.strongest_service) return false;
    const services = formData.strongest_service.split(',').map(s => s.trim()).filter(s => s);
    return services.includes(serviceName);
  };

  // Helper to get array of selected strongest services
  const getSelectedServicesArray = () => {
    if (!formData.strongest_service) return [];
    return formData.strongest_service.split(',').map(s => s.trim()).filter(s => s);
  };

  const handleStrongestServiceCheckboxChange = (serviceName) => {
    const currentServices = getSelectedServicesArray();
    let updatedServices;
    if (currentServices.includes(serviceName)) {
      updatedServices = currentServices.filter(s => s !== serviceName);
    } else {
      updatedServices = [...currentServices, serviceName];
    }
    const servicesString = updatedServices.join(',');
    setFormData(prev => ({
      ...prev,
      strongest_service: servicesString
    }));
  };

  // Handle checkbox change for special services
  const handleSpecialServiceCheckboxChange = (serviceName) => {
    setFormData(prev => {
      const currentString = prev.special_service || '';
      const currentServices = currentString.split(';').map(s => s.trim()).filter(s => s);

      let updatedServices;
      if (currentServices.includes(serviceName)) {
        // Remove service
        updatedServices = currentServices.filter(s => s !== serviceName);
      } else {
        // Add service
        updatedServices = [...currentServices, serviceName];
      }

      return {
        ...prev,
        special_service: updatedServices.join('; ')
      };
    });
  };

  const resetForm = (options = {}) => {
    setFormData({ ...INITIAL_FORM_DATA });
    if (!options.preserveMessage) {
      setMessage(null);
      setMessageType('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields (all fields except remark and city_port - now using multi-select)
    const requiredFields = [
      { field: 'country', label: 'Country' },
      { field: 'agent_name', label: 'Agent Name' },
      { field: 'network', label: 'Network' },
      { field: 'class', label: 'Class' },
      { field: 'pic_overseas', label: 'PIC Overseas' },
      { field: 'email_pic', label: 'Email PIC' },
      { field: 'phone_number', label: 'Phone Number' },
      { field: 'mbl_information', label: 'MBL Information' },
      { field: 'manager', label: 'Manager' },
      { field: 'email_manager', label: 'Email Manager' },
      { field: 'phone_manager', label: 'Phone Manager' }
    ];

    const emptyFields = requiredFields.filter(({ field }) => !formData[field] || !formData[field].toString().trim());

    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.map(({ label }) => label).join(', ');
      setMessage(`Please fill in all required fields: ${fieldNames}`);
      setMessageType('error');
      return;
    }

    if (formData.short_name && formData.short_name.trim().length > 50) {
      setMessage('Short Name must be at most 50 characters.');
      setMessageType('error');
      return;
    }

    // Validate at least one special service is selected
    if (!formData.special_service || formData.special_service.trim() === '') {
      setMessage('Please select at least one Special Service.');
      setMessageType('error');
      return;
    }

    if (formData.suspended && !formData.suspended_remark?.trim()) {
      setMessage('Suspended remark is required when Suspended is enabled.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      // Use appropriate service based on activeTab
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.createAgent(formData);
      const agentId = response.data?.agentId;

      // Assign city ports if any selected and agent was created
      // Note: New agents are inserted into pending_uploads, so use the pending API
      if (agentId && selectedCityPorts.length > 0) {
        try {
          const cityPortEndpoint = activeTab === 'branch'
            ? `${API_URL}/api/city-ports/branch-pending/${agentId}/assign`
            : `${API_URL}/api/city-ports/pending/${agentId}/assign`;
          await axios.post(cityPortEndpoint, {
            cityPortIds: selectedCityPorts
          }, { withCredentials: true });
        } catch (cpError) {
          console.error('Error assigning city ports:', cpError);
        }
      }

      // Delete used vision batch if any
      if (activeBatchId) {
        try {
          await deleteBatch(activeBatchId);
          setSavedBatches(prev => prev.filter(b => b.id !== activeBatchId));
          setActiveBatchId(null);
        } catch (e) {
          console.error('Failed to delete vision batch:', e);
        }
      }

      const tabLabel = activeTab === 'branch' ? 'Branch agent' : 'Agent';
      setMessage(`${tabLabel} created successfully! Your submission is now pending approval.`);
      setMessageType('success');
      setSelectedCityPorts([]); // Reset city ports selection
      resetForm({ preserveMessage: true });

    } catch (error) {
      console.error('Create agent error:', error);
      setMessage(error.response?.data?.error || 'Failed to create agent. Please try again.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- File Upload Handlers ---
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];
      if (allowedTypes.includes(selectedFile.type)) {
        setUploadFile(selectedFile);
        setUploadMessage(null);
      } else {
        setUploadFile(null);
        setUploadMessage('Please select a valid Excel (.xlsx, .xls) or CSV file.');
        setUploadMessageType('error');
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadFile) {
      setUploadMessage('Please select a file to upload.');
      setUploadMessageType('error');
      return;
    }

    setUploading(true);
    setUploadMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('table', activeTab === 'branch' ? 'agentlist_branch' : 'agentlist');

      // Use appropriate service based on activeTab
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.uploadFile(formData);

      const { recordsProcessed, recordsUpdated, recordsInserted } = response.data;
      const updateInfo = recordsUpdated > 0 ? `Updated ${recordsUpdated} existing records` : '';
      const insertInfo = recordsInserted > 0 ? `Inserted ${recordsInserted} new records` : '';
      const separator = (updateInfo && insertInfo) ? ', ' : '';

      const tabLabel = activeTab === 'branch' ? 'Branch upload' : 'Upload';
      setUploadMessage(`${tabLabel} successful! Processed ${recordsProcessed} records: ${updateInfo}${separator}${insertInfo}.`);
      setUploadMessageType('success');
      setUploadFile(null);

      const fileInput = document.getElementById('bulkFileInput');
      if (fileInput) fileInput.value = '';

      if (response.data.errors && response.data.errors.length > 0) {
        setUploadMessage(prev => prev + ` Note: ${response.data.errors.length} records had errors.`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadMessage(error.response?.data?.error || 'Upload failed. Please try again.');
      setUploadMessageType('error');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadExampleExcel = async () => {
    try {
      const success = await downloadExampleExcel();
      if (success) {
        setUploadMessage('Example Excel file downloaded successfully!');
        setUploadMessageType('success');
        setTimeout(() => setUploadMessage(null), 3000);
      } else {
        setUploadMessage('Failed to download example file. Please try again.');
        setUploadMessageType('error');
      }
    } catch {
      setUploadMessage('Failed to download example file. Please try again.');
      setUploadMessageType('error');
    }
  };

  const handleDownloadExampleCSV = () => {
    const success = downloadExampleCSV();
    if (success) {
      setUploadMessage('Example CSV file downloaded successfully!');
      setUploadMessageType('success');
      setTimeout(() => setUploadMessage(null), 3000);
    } else {
      setUploadMessage('Failed to download example file. Please try again.');
      setUploadMessageType('error');
    }
  };

  // Prepare country options for Select
  const countryOptions = countries.map(country => ({
    value: country.name,
    label: country.name
  }));

  return (
    <Box minH="100vh" bg={bgColor} py={8}>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          <Box textAlign="center" py={4}>
            <Heading as="h1" size="2xl" color="purple.600" mb={3}>Create New Agent</Heading>
            <Text fontSize="xl" color="gray.500">
              Add a new agent to the directory. Require admin approval.
            </Text>
            <Button
              mt={3}
              colorPalette="teal"
              variant="outline"
              onClick={() => setShowVisionImport(true)}
            >
              <FiCamera /> Import from Business Card
              {savedBatches.length > 0 && (
                <Badge colorPalette="purple" ml={2}>{savedBatches.length} saved</Badge>
              )}
            </Button>
          </Box>

          {/* AI Vision Import Modal */}
          {showVisionImport && (
            <Card.Root shadow="lg" borderRadius="xl" bg={cardBg} borderTop="4px" borderTopColor="teal.500" mb={4}>
              <Card.Header borderBottom="1px" borderColor="gray.100" pb={4}>
                <Flex justify="space-between" align="center">
                  <Heading size="md" color="teal.700">AI Vision - Import from Business Card</Heading>
                  <Button size="sm" variant="ghost" onClick={() => setShowVisionImport(false)}>Close</Button>
                </Flex>
              </Card.Header>
              <Card.Body p={6}>
                <VStack spacing={4} align="stretch">
                  <Text fontSize="sm" color="gray.600">
                    Upload business card images and AI will extract agent information automatically. Supports up to 5 images.
                  </Text>

                  {/* File Upload Area */}
                  <Box
                    border="2px dashed"
                    borderColor={visionFiles.length > 0 ? 'teal.300' : 'gray.300'}
                    borderRadius="lg"
                    p={6}
                    textAlign="center"
                    cursor="pointer"
                    _hover={{ borderColor: 'teal.400', bg: 'teal.50' }}
                    onClick={() => visionFileInputRef.current?.click()}
                    transition="all 0.2s"
                  >
                    <input
                      ref={visionFileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleVisionFileSelect}
                      style={{ display: 'none' }}
                    />
                    <Icon fontSize="2xl" color="teal.500" mb={2}>
                      <FiCamera />
                    </Icon>
                    <Text fontWeight="bold" color="gray.600">
                      {visionFiles.length > 0
                        ? `${visionFiles.length} image(s) selected`
                        : 'Click or drag business card images here'}
                    </Text>
                    <Text fontSize="xs" color="gray.400" mt={1}>PNG, JPG, WEBP - Max 10MB each</Text>
                  </Box>

                  {/* Selected Files Preview */}
                  {visionFiles.length > 0 && (
                    <HStack spacing={3} overflowX="auto" py={2}>
                      {visionFiles.map((file, i) => (
                        <Box key={i} position="relative" flexShrink={0}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            style={{
                              width: '120px',
                              height: '80px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '2px solid #E2E8F0'
                            }}
                          />
                          <Text fontSize="xs" color="gray.500" mt={1} textAlign="center" isTruncated maxW="120px">
                            {file.name}
                          </Text>
                        </Box>
                      ))}
                    </HStack>
                  )}

                  {/* Extract Button */}
                  <Button
                    colorPalette="teal"
                    onClick={handleVisionExtract}
                    disabled={visionFiles.length === 0 || visionProcessing}
                    loading={visionProcessing}
                    loadingText="AI is extracting data..."
                  >
                    <FiCheckCircle /> Extract Agent Information
                  </Button>

                  {/* Error */}
                  {visionError && (
                    <Alert.Root status="error" borderRadius="md">
                      <Alert.Indicator />
                      <Alert.Title>{visionError}</Alert.Title>
                    </Alert.Root>
                  )}

                  {/* Saved Batches */}
                  {savedBatches.length > 0 && (
                    <Box>
                      <Heading size="sm" color="purple.600" mb={2}>Saved Extractions ({savedBatches.length})</Heading>
                      <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                        {savedBatches.map((batch, idx) => (
                          <Box key={batch.id}>
                            <Flex
                              align="center"
                              gap={3}
                              px={3}
                              py={2}
                              bg={activeBatchId === batch.id ? 'purple.50' : idx % 2 === 0 ? 'white' : 'gray.50'}
                              borderBottom="1px solid"
                              borderColor={activeBatchId === batch.id ? 'purple.200' : 'gray.100'}
                              _hover={{ bg: 'purple.50' }}
                              cursor="pointer"
                              onClick={() => {
                                if (activeBatchId === batch.id) {
                                  setActiveBatchId(null);
                                } else {
                                  setActiveBatchId(batch.id);
                                  applyVisionResult(batch.extracted_data, batch.id);
                                }
                              }}
                            >
                              <Text fontSize="sm" fontWeight="bold" minW="100px" isTruncated>
                                {batch.extracted_data?.agent_name || batch.filename || 'Unnamed'}
                              </Text>
                              <Badge colorPalette="gray" fontSize="xs" flexShrink={0}>
                                {batch.extracted_data?.country || '-'}
                              </Badge>
                              <Text fontSize="xs" color="gray.500" isTruncated flex={1}>
                                {batch.extracted_data?.pic_overseas || ''}
                              </Text>
                              <Text fontSize="xs" color="gray.400" flexShrink={0}>
                                {new Date(batch.created_at).toLocaleDateString()}
                              </Text>
                              <Button
                                size="xs"
                                colorPalette="red"
                                variant="ghost"
                                flexShrink={0}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await deleteBatch(batch.id);
                                    setSavedBatches(prev => prev.filter(b => b.id !== batch.id));
                                    if (activeBatchId === batch.id) setActiveBatchId(null);
                                  } catch (err) {
                                    console.error('Failed to delete batch:', err);
                                  }
                                }}
                              >
                                ✕
                              </Button>
                            </Flex>
                            {/* Expanded detail card */}
                            {activeBatchId === batch.id && (
                              <Box px={4} py={3} bg="purple.50" borderBottom="1px solid" borderColor="purple.200">
                                <SimpleGrid columns={{ base: 1, md: 2 }} gap={2} mb={3}>
                                  <Text fontSize="sm"><strong>Company:</strong> {batch.extracted_data?.agent_name || '-'}</Text>
                                  <Text fontSize="sm"><strong>Short Name:</strong> {batch.extracted_data?.short_name || '-'}</Text>
                                  <Text fontSize="sm"><strong>Country:</strong> {batch.extracted_data?.country || '-'}</Text>
                                  <Text fontSize="sm"><strong>City/Port:</strong> {batch.extracted_data?.city_port || '-'}</Text>
                                  <Text fontSize="sm"><strong>PIC:</strong> {batch.extracted_data?.pic_overseas || '-'}</Text>
                                  <Text fontSize="sm"><strong>Email:</strong> {batch.extracted_data?.email_pic || '-'}</Text>
                                  <Text fontSize="sm"><strong>Phone:</strong> {batch.extracted_data?.phone_number || '-'}</Text>
                                  <Text fontSize="sm"><strong>Manager:</strong> {batch.extracted_data?.manager || '-'}</Text>
                                  <Text fontSize="sm"><strong>Email Mgr:</strong> {batch.extracted_data?.email_manager || '-'}</Text>
                                  <Text fontSize="sm"><strong>Phone Mgr:</strong> {batch.extracted_data?.phone_manager || '-'}</Text>
                                  <Text fontSize="sm"><strong>Network:</strong> {batch.extracted_data?.network || '-'}</Text>
                                  <Text fontSize="sm"><strong>Services:</strong> {[
                                    batch.extracted_data?.fcl === 'YES' && 'FCL',
                                    batch.extracted_data?.lcl === 'YES' && 'LCL',
                                    batch.extracted_data?.air === 'YES' && 'AIR',
                                    batch.extracted_data?.inland === 'YES' && 'INLAND'
                                  ].filter(Boolean).join(', ') || 'None'}</Text>
                                  <Text fontSize="sm"><strong>Strongest:</strong> {batch.extracted_data?.strongest_service || '-'}</Text>
                                  <Text fontSize="sm"><strong>Special:</strong> {batch.extracted_data?.special_service || '-'}</Text>
                                </SimpleGrid>
                                {batch.extracted_data?.remark && (
                                  <Text fontSize="sm"><strong>Remark:</strong> {batch.extracted_data.remark}</Text>
                                )}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                </VStack>
              </Card.Body>
            </Card.Root>
          )}

          {/* Main/Branch Tabs */}
          <Tabs.Root defaultValue="main" value={activeTab} onValueChange={handleTabChange}>
            <Tabs.List mb={4}>
              {canEditMain() && (
                <Tabs.Trigger value="main" px={6} py={2}>
                  <Text fontWeight="medium">Main Agentlist</Text>
                </Tabs.Trigger>
              )}
              {canEditBranch() && (
                <Tabs.Trigger value="branch" px={6} py={2}>
                  <Text fontWeight="medium">Branch Agentlist</Text>
                </Tabs.Trigger>
              )}
              <Tabs.Indicator />
            </Tabs.List>
          </Tabs.Root>

          {/* Current Tab Badge */}
          <Badge colorPalette={activeTab === 'branch' ? 'orange' : 'purple'} fontSize="md" px={4} py={2} borderRadius="full" alignSelf="center">
            Creating {activeTab === 'branch' ? 'Branch' : 'Main'} Agent
          </Badge>

          {/* Main Form Card */}
          <Card.Root shadow="lg" borderRadius="xl" bg={cardBg} borderTop="4px" borderTopColor="purple.500">
            <Card.Header borderBottom="1px" borderColor="gray.100" pb={4}>
              <Heading size="md" color="gray.700">Agent Information</Heading>
            </Card.Header>
            <Card.Body p={8}>


              <form onSubmit={handleSubmit}>
                <VStack spacing={8} align="stretch">

                  {/* Basic Info */}
                  <Box>
                    <Heading size="sm" color="purple.600" mb={4} textTransform="uppercase">Basic Information</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={8} rowGap={6}>
                      <Box {...(fillingFields['country'] ? { p: 1, borderRadius: 'md', ...getFieldVisionStyle('country') } : {})}>
                        <Text fontWeight="bold" mb={2}>Country</Text>
                        <Select
                          name="country"
                          value={countryOptions.find(option => option.value === formData.country)}
                          onChange={handleCountryChange}
                          options={countryOptions}
                          placeholder="Select country..."
                          isClearable
                          isSearchable
                          chakraStyles={{
                            control: (provided) => ({ ...provided, borderColor: 'gray.200', _hover: { borderColor: 'gray.400' } })
                          }}
                        />
                        {countries.length === 0 && (
                          <Text fontSize="xs" color="red.500" mt={1}>No countries found. Add below.</Text>
                        )}
                      </Box>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Agent Name</Text>
                        <Input
                          name="agent_name"
                          value={formData.agent_name}
                          onChange={handleInputChange}
                          placeholder="Enter agent name"
                          {...getFieldVisionStyle('agent_name')}
                        />
                      </Box>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Short Name</Text>
                        <Input
                          name="short_name"
                          value={formData.short_name}
                          onChange={handleInputChange}
                          placeholder="Enter short name (optional, max 50)"
                          maxLength={50}
                          {...getFieldVisionStyle('short_name')}
                        />
                      </Box>
                    </SimpleGrid>
                  </Box>

                  <Separator />

                  {/* Additional Info */}
                  <Box>
                    <Heading size="sm" color="purple.600" mb={4} textTransform="uppercase">Additional Information</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={8} rowGap={6}>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Network</Text>
                        <Input name="network" value={formData.network} onChange={handleInputChange} placeholder="Network name" {...getFieldVisionStyle('network')} />
                      </Box>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Class</Text>
                        <Input type="number" name="class" value={formData.class} onChange={handleInputChange} placeholder="Class number" />
                      </Box>
                    </SimpleGrid>
                    <Box mt={6}>
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text fontWeight="bold">City/Port</Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="purple"
                          onClick={() => setShowAddCityPort(!showAddCityPort)}
                        >
                          {showAddCityPort ? '✕ Cancel' : '+ Add New'}
                        </Button>
                      </Flex>

                      {/* Inline Add City Port Form */}
                      {/* Inline Add City Port Form */}
                      {showAddCityPort && (
                        <Box bg="purple.50" p={3} borderRadius="md" mb={2} border="1px dashed" borderColor="purple.300">
                          <Text fontWeight="bold" mb={2} color="purple.700" fontSize="sm">New City Port</Text>
                          <Input
                            size="sm"
                            placeholder="City Port Name *"
                            value={newCityPortData.name}
                            onChange={(e) => setNewCityPortData({ ...newCityPortData, name: e.target.value })}
                            bg="white"
                            mb={3}
                          />

                          <Flex justify="space-between" align="center" mb={1}>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.600">PIC(s)</Text>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorPalette="purple"
                              onClick={() => setNewCityPortPics([...newCityPortPics, { pic_name: '', pic_email: '', pic_phone: '' }])}
                            >
                              <FaPlus style={{ marginRight: '4px' }} /> Add PIC
                            </Button>
                          </Flex>

                          <VStack spacing={2} align="stretch" mb={3}>
                            {newCityPortPics.map((pic, index) => (
                              <Flex key={index} gap={1} align="center">
                                <Input
                                  size="sm"
                                  placeholder="Name"
                                  value={pic.pic_name}
                                  onChange={(e) => {
                                    const updated = [...newCityPortPics];
                                    updated[index].pic_name = e.target.value;
                                    setNewCityPortPics(updated);
                                  }}
                                  bg="white"
                                />
                                <Input
                                  size="sm"
                                  placeholder="Email"
                                  value={pic.pic_email}
                                  onChange={(e) => {
                                    const updated = [...newCityPortPics];
                                    updated[index].pic_email = e.target.value;
                                    setNewCityPortPics(updated);
                                  }}
                                  bg="white"
                                />
                                <Input
                                  size="sm"
                                  placeholder="Phone"
                                  value={pic.pic_phone}
                                  onChange={(e) => {
                                    const updated = [...newCityPortPics];
                                    updated[index].pic_phone = e.target.value;
                                    setNewCityPortPics(updated);
                                  }}
                                  bg="white"
                                />
                                {newCityPortPics.length > 1 && (
                                  <IconButton
                                    aria-label="Remove"
                                    size="xs"
                                    colorPalette="red"
                                    variant="ghost"
                                    onClick={() => {
                                      if (newCityPortPics.length > 1) {
                                        setNewCityPortPics(newCityPortPics.filter((_, i) => i !== index));
                                      }
                                    }}
                                  >
                                    <FiTrash />
                                  </IconButton>
                                )}
                              </Flex>
                            ))}
                          </VStack>

                          <Button
                            size="sm"
                            colorScheme="purple"
                            onClick={handleAddCityPortInline}
                            isLoading={addingCityPort}
                            isDisabled={!newCityPortData.name.trim()}
                            width="full"
                            variant="solid"
                          >
                            Create & Select
                          </Button>
                        </Box>
                      )}

                      <Select
                        isMulti
                        value={cityPorts
                          .filter(cp => selectedCityPorts.includes(cp.id))
                          .map(cp => ({
                            value: cp.id,
                            label: cp.name,
                          }))}
                        onChange={(selected) => {
                          const ids = selected ? selected.map(s => s.value) : [];
                          setSelectedCityPorts(ids);
                          loadPicsForCityPorts(ids);
                        }}
                        options={cityPorts.map(cp => ({
                          value: cp.id,
                          label: cp.name + (cp.pic_name ? ` (PIC: ${cp.pic_name})` : ''),
                        }))}
                        placeholder="Select city/ports..."
                        isClearable
                        isSearchable
                        closeMenuOnSelect={false}
                        chakraStyles={{
                          multiValue: (provided) => ({
                            ...provided,
                            bg: 'purple.100',
                          }),
                          multiValueLabel: (provided) => ({
                            ...provided,
                            color: 'purple.700',
                          }),
                        }}
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>Select existing or create new city ports with PIC info.</Text>

                      {/* PIC Management for Selected City Ports */}
                      {selectedCityPorts.length > 0 && (
                        <Box mt={3} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                          <Text fontSize="sm" fontWeight="bold" color="purple.600" mb={2}>
                            PICs of Selected City Ports
                          </Text>
                          <VStack gap={3} align="stretch">
                            {selectedCityPorts.map(cpId => {
                              const cityPort = cityPorts.find(cp => cp.id === cpId);
                              const pics = cityPortPicsMap[cpId] || [];
                              return (
                                <Box key={cpId} p={2} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                                  <Flex justify="space-between" align="center" mb={2}>
                                    <Text fontSize="sm" fontWeight="semibold" color="purple.700">
                                      {cityPort?.name || `Port #${cpId}`}
                                    </Text>
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      colorPalette="purple"
                                      onClick={() => {
                                        setAddingPicToCityPort(addingPicToCityPort === cpId ? null : cpId);
                                        setNewPicData({ pic_name: '', pic_email: '', pic_phone: '' });
                                      }}
                                    >
                                      <FaPlus style={{ marginRight: '4px' }} /> Add PIC
                                    </Button>
                                  </Flex>

                                  {/* Add new PIC form */}
                                  {addingPicToCityPort === cpId && (
                                    <Box mb={2} p={2} bg="purple.50" borderRadius="md">
                                      <Flex gap={1} mb={2}>
                                        <Input
                                          size="sm"
                                          placeholder="Name *"
                                          value={newPicData.pic_name}
                                          onChange={(e) => setNewPicData({ ...newPicData, pic_name: e.target.value })}
                                          bg="white"
                                        />
                                        <Input
                                          size="sm"
                                          placeholder="Email"
                                          value={newPicData.pic_email}
                                          onChange={(e) => setNewPicData({ ...newPicData, pic_email: e.target.value })}
                                          bg="white"
                                        />
                                        <Input
                                          size="sm"
                                          placeholder="Phone"
                                          value={newPicData.pic_phone}
                                          onChange={(e) => setNewPicData({ ...newPicData, pic_phone: e.target.value })}
                                          bg="white"
                                        />
                                      </Flex>
                                      <HStack>
                                        <Button
                                          size="xs"
                                          colorPalette="purple"
                                          onClick={() => handleAddPicToExisting(cpId)}
                                          loading={savingPic}
                                          disabled={!newPicData.pic_name.trim()}
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          size="xs"
                                          variant="ghost"
                                          onClick={() => setAddingPicToCityPort(null)}
                                        >
                                          Cancel
                                        </Button>
                                      </HStack>
                                    </Box>
                                  )}

                                  {/* List of PICs */}
                                  {pics.length === 0 ? (
                                    <Text fontSize="xs" color="gray.500" fontStyle="italic">No PICs</Text>
                                  ) : (
                                    <VStack gap={1} align="stretch">
                                      {pics.map(pic => (
                                        <Box key={pic.id}>
                                          {editingPic && editingPic.picId === pic.id ? (
                                            <Box p={2} bg="blue.50" borderRadius="md">
                                              <Flex gap={1} mb={2}>
                                                <Input
                                                  size="sm"
                                                  placeholder="Name *"
                                                  value={editingPic.pic_name}
                                                  onChange={(e) => setEditingPic({ ...editingPic, pic_name: e.target.value })}
                                                  bg="white"
                                                />
                                                <Input
                                                  size="sm"
                                                  placeholder="Email"
                                                  value={editingPic.pic_email}
                                                  onChange={(e) => setEditingPic({ ...editingPic, pic_email: e.target.value })}
                                                  bg="white"
                                                />
                                                <Input
                                                  size="sm"
                                                  placeholder="Phone"
                                                  value={editingPic.pic_phone}
                                                  onChange={(e) => setEditingPic({ ...editingPic, pic_phone: e.target.value })}
                                                  bg="white"
                                                />
                                              </Flex>
                                              <HStack>
                                                <Button
                                                  size="xs"
                                                  colorPalette="blue"
                                                  onClick={handleSavePic}
                                                  loading={savingPic}
                                                  disabled={!editingPic.pic_name.trim()}
                                                >
                                                  Save
                                                </Button>
                                                <Button
                                                  size="xs"
                                                  variant="ghost"
                                                  onClick={() => setEditingPic(null)}
                                                >
                                                  Cancel
                                                </Button>
                                              </HStack>
                                            </Box>
                                          ) : (
                                            <Flex
                                              justify="space-between"
                                              align="center"
                                              p={1}
                                              borderRadius="sm"
                                              _hover={{ bg: 'gray.100' }}
                                            >
                                              <Box>
                                                <Text fontSize="xs" fontWeight="medium">{pic.pic_name}</Text>
                                                <Text fontSize="xs" color="gray.500">
                                                  {[pic.pic_email, pic.pic_phone].filter(Boolean).join(' | ') || 'No contact info'}
                                                </Text>
                                              </Box>
                                              <HStack gap={1}>
                                                <IconButton
                                                  aria-label="Edit"
                                                  size="xs"
                                                  variant="ghost"
                                                  colorPalette="blue"
                                                  onClick={() => handleEditPic(cpId, pic)}
                                                >
                                                  <FiEdit2 />
                                                </IconButton>
                                                <IconButton
                                                  aria-label="Delete"
                                                  size="xs"
                                                  variant="ghost"
                                                  colorPalette="red"
                                                  onClick={() => handleDeletePic(pic.id, cpId)}
                                                >
                                                  <FiTrash />
                                                </IconButton>
                                              </HStack>
                                            </Flex>
                                          )}
                                        </Box>
                                      ))}
                                    </VStack>
                                  )}
                                </Box>
                              );
                            })}
                          </VStack>
                        </Box>
                      )}
                    </Box>

                    <Box mt={6} mb={4} {...(fillingFields['special_service'] ? { p: 2, borderRadius: 'md', ...getFieldVisionStyle('special_service') } : {})}>
                      <Text fontWeight="bold" mb={2}>Special Service (Multi-select)</Text>
                      <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                        {specialServiceOptions.length === 0 ? (
                          <Text fontSize="sm" color="gray.500">No options available. Manage below.</Text>
                        ) : (
                          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4} rowGap={4}>
                            {specialServiceOptions.map(option => (
                              <Checkbox.Root
                                key={option.id}
                                checked={isSpecialServiceSelected(option.option_name)}
                                onCheckedChange={() => handleSpecialServiceCheckboxChange(option.option_name)}
                                colorPalette="purple"
                              >
                                <Checkbox.HiddenInput />
                                <Checkbox.Control>
                                  <Checkbox.Indicator />
                                </Checkbox.Control>
                                <Checkbox.Label>{option.option_name}</Checkbox.Label>
                              </Checkbox.Root>
                            ))}
                          </SimpleGrid>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Separator />

                  {/* Contact Info */}
                  <Box>
                    <Heading size="sm" color="purple.600" mb={4} textTransform="uppercase">Contact Information</Heading>
                    <SimpleGrid columns={{ base: 1, md: 3 }} gap={8} rowGap={6} mb={6}>
                      <Box>
                        <Text fontWeight="bold" mb={2}>PIC Overseas</Text>
                        <Input name="pic_overseas" value={formData.pic_overseas} onChange={handleInputChange} {...getFieldVisionStyle('pic_overseas')} />
                      </Box>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Email PIC</Text>
                        <Input name="email_pic" value={formData.email_pic} onChange={handleInputChange} placeholder="email@example.com" {...getFieldVisionStyle('email_pic')} />
                      </Box>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Phone Number</Text>
                        <Input name="phone_number" value={formData.phone_number} onChange={handleInputChange} {...getFieldVisionStyle('phone_number')} />
                      </Box>
                    </SimpleGrid>
                    <Box>
                      <Text fontWeight="bold" mb={2}>MBL Information</Text>
                      <Textarea name="mbl_information" value={formData.mbl_information} onChange={handleInputChange} placeholder="Enter MBL Info" {...getFieldVisionStyle('mbl_information')} />
                    </Box>
                  </Box>

                  {/* Manager Info */}
                  <Box>
                    <Heading size="sm" color="purple.600" mb={4} textTransform="uppercase">Manager Contact</Heading>
                    <SimpleGrid columns={{ base: 1, md: 3 }} gap={8} rowGap={6}>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Manager Name</Text>
                        <Input name="manager" value={formData.manager} onChange={handleInputChange} {...getFieldVisionStyle('manager')} />
                      </Box>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Email Manager</Text>
                        <Input name="email_manager" value={formData.email_manager} onChange={handleInputChange} {...getFieldVisionStyle('email_manager')} />
                      </Box>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Phone Manager</Text>
                        <Input name="phone_manager" value={formData.phone_manager} onChange={handleInputChange} {...getFieldVisionStyle('phone_manager')} />
                      </Box>
                    </SimpleGrid>
                  </Box>

                  <Separator />

                  {/* Services & Classification */}
                  <SimpleGrid columns={{ base: 1, lg: 2 }} gap={10} rowGap={8}>
                    <Box>
                      <Heading size="sm" color="purple.600" mb={4} textTransform="uppercase">Services Available</Heading>
                      <SimpleGrid columns={3} gap={6} rowGap={6} mb={6}>
                        {['fcl', 'lcl', 'air'].map(service => (
                          <Box key={service} {...(fillingFields[service] ? { p: 1, borderRadius: 'md', ...getFieldVisionStyle(service) } : {})}>
                            <Text fontWeight="bold" mb={2}>{service.toUpperCase()}</Text>
                            <NativeSelect.Root>
                              <NativeSelect.Field name={service} value={formData[service]} onChange={handleInputChange}>
                                <option value="NO">No</option>
                                <option value="YES">Yes</option>
                              </NativeSelect.Field>
                              <NativeSelect.Indicator />
                            </NativeSelect.Root>
                          </Box>
                        ))}
                      </SimpleGrid>
                      <HStack spacing={6} mb={6}>
                        <Checkbox.Root
                          checked={formData.consol_export}
                          onCheckedChange={(details) =>
                            setFormData(prev => ({ ...prev, consol_export: !!details.checked }))
                          }
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                          <Checkbox.Label fontWeight="bold">Consol Export</Checkbox.Label>
                        </Checkbox.Root>
                        <Checkbox.Root
                          checked={formData.consol_import}
                          onCheckedChange={(details) =>
                            setFormData(prev => ({ ...prev, consol_import: !!details.checked }))
                          }
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                          <Checkbox.Label fontWeight="bold">Consol Import</Checkbox.Label>
                        </Checkbox.Root>
                        <Checkbox.Root
                          checked={formData.consol_oversea}
                          onCheckedChange={(details) =>
                            setFormData(prev => ({ ...prev, consol_oversea: !!details.checked }))
                          }
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                          <Checkbox.Label fontWeight="bold">Oversea</Checkbox.Label>
                        </Checkbox.Root>
                      </HStack>
                      <Box {...(fillingFields['strongest_service'] ? { p: 2, borderRadius: 'md', ...getFieldVisionStyle('strongest_service') } : {})}>
                        <Text fontWeight="bold" mb={2} display="flex" alignItems="center" gap={2}>⭐ Strongest Services</Text>
                        <VStack align="start" spacing={2}>
                          <Checkbox.Root
                            checked={isServiceSelected('FCL')}
                            onCheckedChange={() => handleStrongestServiceCheckboxChange('FCL')}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                            <Checkbox.Label display="flex" alignItems="center" gap={2}>
                              <Icon as={LuContainer} /> FCL (Full Container Load)
                            </Checkbox.Label>
                          </Checkbox.Root>
                          <Checkbox.Root
                            checked={isServiceSelected('LCL')}
                            onCheckedChange={() => handleStrongestServiceCheckboxChange('LCL')}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                            <Checkbox.Label display="flex" alignItems="center" gap={2}>
                              <Icon as={LuPackage} /> LCL (Less Container Load)
                            </Checkbox.Label>
                          </Checkbox.Root>
                          <Checkbox.Root
                            checked={isServiceSelected('AIR')}
                            onCheckedChange={() => handleStrongestServiceCheckboxChange('AIR')}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                            <Checkbox.Label display="flex" alignItems="center" gap={2}>
                              <Icon as={LuPlane} /> AIR (Air Freight)
                            </Checkbox.Label>
                          </Checkbox.Root>
                        </VStack>
                      </Box>
                    </Box>

                    <Box>
                      <Heading size="sm" color="purple.600" mb={4} textTransform="uppercase">Classification</Heading>
                      <SimpleGrid columns={2} gap={6} rowGap={6} mb={6}>
                        <Box>
                          <Text fontWeight="bold" mb={2}>Status</Text>
                          <NativeSelect.Root>
                            <NativeSelect.Field name="status" value={formData.status} onChange={handleInputChange}>
                              <option value="APPROVED">Approved</option>
                              <option value="POTENTIAL">Potential</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" mb={2}>Contract</Text>
                          <NativeSelect.Root>
                            <NativeSelect.Field name="contract" value={formData.contract} onChange={handleInputChange}>
                              <option value="NO">No</option>
                              <option value="YES">Yes</option>
                              <option value="PENDING">Pending</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Box>
                      </SimpleGrid>
                      <SimpleGrid columns={2} gap={6} rowGap={6}>
                        <Box>
                          <Text fontWeight="bold" mb={2}>Additional</Text>
                          <NativeSelect.Root>
                            <NativeSelect.Field name="additional" value={formData.additional} onChange={handleInputChange}>
                              <option value="">-- None --</option>
                              <option value="AGENT CONSIGNED">Agent Consigned</option>
                              <option value="QTM CONSIGNED">QTM Consigned</option>
                              <option value="BOTH SIDE">Both Side</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" mb={2}>Membership Validity</Text>
                          <Input type="date" name="membership_validity" value={formData.membership_validity} onChange={handleInputChange} />
                        </Box>
                        <Box>
                          <Checkbox.Root
                            checked={formData.suspended}
                            onCheckedChange={(details) =>
                              setFormData(prev => ({
                                ...prev,
                                suspended: !!details.checked,
                                suspended_remark: details.checked ? prev.suspended_remark : ''
                              }))
                            }
                            colorPalette="red"
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control>
                              <Checkbox.Indicator />
                            </Checkbox.Control>
                            <Checkbox.Label>Suspended</Checkbox.Label>
                          </Checkbox.Root>
                        </Box>
                        {formData.suspended && (
                          <Box>
                            <Text fontWeight="bold" mb={2}>Suspended Remark *</Text>
                            <Textarea
                              name="suspended_remark"
                              value={formData.suspended_remark}
                              onChange={handleInputChange}
                              rows={2}
                              placeholder="Enter suspended remark"
                              required
                            />
                          </Box>
                        )}
                      </SimpleGrid>
                    </Box>
                  </SimpleGrid>

                  <Separator />

                  <Box mb={4}>
                    <Text fontWeight="bold" mb={2}>Remark</Text>
                    <Textarea name="remark" value={formData.remark} onChange={handleInputChange} rows={3} {...getFieldVisionStyle('remark')} />
                  </Box>

                  <Flex gap={4}>
                    <Button variant="outline" onClick={() => resetForm()} isDisabled={isSubmitting}>Clear Form</Button>
                    <Spacer />
                    <Button type="submit" colorScheme="purple" isLoading={isSubmitting} isDisabled={isSubmitting} loadingText="Creating...">Create Agent</Button>
                  </Flex>
                  {message && (
                    <Alert.Root status={messageType === 'success' ? 'success' : 'error'} borderRadius="md" mt={4}>
                      <Alert.Indicator />
                      {message}
                    </Alert.Root>
                  )}
                </VStack>
              </form>
            </Card.Body>
          </Card.Root>

          {/* Admin Management Section */}
          {hasRole(['Admin', 'Editor']) && (
          <Accordion.Root collapsible>
            {/* Country Manager */}
            <Accordion.Item value="country-manager" border="none" mb={4}>
              <h2>
                <Accordion.ItemTrigger bg="white" p={4} borderRadius="xl" shadow="sm" _hover={{ bg: 'gray.50' }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="gray.700">Manage Countries</Heading>
                    <Text fontSize="sm" color="gray.500">Editor & Admin Only</Text>
                  </Box>
                  <Accordion.ItemIndicator />
                </Accordion.ItemTrigger>
              </h2>
              <Accordion.ItemContent pb={4} bg="white" mt={2} borderRadius="xl" shadow="md">
                {countryMessage && (
                  <Alert.Root status={countryMessageType === 'success' ? 'success' : 'error'} mb={4} borderRadius="md">
                    <Alert.Indicator /> {countryMessage}
                  </Alert.Root>
                )}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <Heading size="sm" mb={3}>Add New Country</Heading>
                    <form onSubmit={handleAddCountry}>
                      <VStack spacing={3}>
                        <Input placeholder="Country Name" value={newCountry.name} onChange={(e) => setNewCountry({ ...newCountry, name: e.target.value })} bg="white" required />
                        <Input placeholder="Code (e.g. VN)" value={newCountry.code} onChange={(e) => setNewCountry({ ...newCountry, code: e.target.value })} bg="white" />
                        <Button type="submit" colorScheme="blue" size="sm" width="full">Add Country</Button>
                      </VStack>
                    </form>
                  </Box>
                  <Box maxH="300px" overflowY="auto">
                    <Heading size="sm" mb={3}>Existing Countries</Heading>
                    <VStack align="stretch" spacing={2}>
                      {countries.map(country => (
                        <Flex key={country.id} p={2} bg={country.is_active ? "white" : "gray.100"} borderWidth="1px" borderRadius="md" justify="space-between" align="center">
                          <Box>
                            <Text fontWeight="bold">{country.name} <Text as="span" fontSize="xs" color="gray.500">{country.code}</Text></Text>
                            {!country.is_active && <Badge colorScheme="yellow">Inactive</Badge>}
                          </Box>
                          <HStack>
                            <Icon as={FiTrash2} color="red.500" cursor="pointer" onClick={() => handleDeleteCountry(country.id, country.name)} />
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                </SimpleGrid>
              </Accordion.ItemContent>
            </Accordion.Item>

            {/* Options Manager */}
            <Accordion.Item value="special-services" border="none">
              <h2>
                <Accordion.ItemTrigger bg="white" p={4} borderRadius="xl" shadow="sm" _hover={{ bg: 'gray.50' }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="gray.700">Manage Special Services</Heading>
                    <Text fontSize="sm" color="gray.500">Editor & Admin Only</Text>
                  </Box>
                  <Accordion.ItemIndicator />
                </Accordion.ItemTrigger>
              </h2>
              <Accordion.ItemContent p={6} bg="white" mt={2} borderRadius="xl" shadow="md">
                {/* Simplified Options Manager Implementation for brevity - assuming similar logic to Country Manager */}
                <Box p={4} bg="gray.50" borderRadius="md" mb={4}>
                  <Heading size="sm" mb={3}>Add Option</Heading>
                  <form onSubmit={handleAddOption}>
                    <HStack gap={4}>
                      <Input placeholder="Option Name" value={newOption.option_name} onChange={(e) => setNewOption({ ...newOption, option_name: e.target.value })} bg="white" required />
                      <Button type="submit" colorScheme="purple">Add</Button>
                    </HStack>
                  </form>
                </Box>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={8} rowGap={6}>
                  {specialServiceOptions.map(option => (
                    <Box key={option.id} p={3} borderWidth="1px" borderRadius="md" bg="white">
                      <Flex justify="space-between" align="center">
                        <Text fontWeight="medium">{option.option_name}</Text>
                        <Icon as={FiTrash2} color="red.500" cursor="pointer" onClick={() => handleDeleteOption(option.id, option.option_name)} />
                      </Flex>
                    </Box>
                  ))}
                </SimpleGrid>
              </Accordion.ItemContent>
            </Accordion.Item>
          </Accordion.Root>
          )}

          {/* Bulk Upload Section */}
          <Card.Root shadow="lg" borderRadius="xl" bg={cardBg} borderTop="4px" borderTopColor="green.500">
            <Card.Header borderBottom="1px" borderColor="gray.100" pb={4}>
              <Heading size="md" color="gray.700" display="flex" alignItems="center" gap={2}>
                <Icon as={FiUpload} /> Bulk Upload
              </Heading>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Upload Excel (.xlsx, .xls) or CSV files to add multiple agents at once.
              </Text>
            </Card.Header>
            <Card.Body p={8}>
              <VStack spacing={6}>
                {/* Drag and Drop Area */}
                <Box
                  border="2px dashed"
                  borderColor={uploadFile ? "green.500" : "gray.300"}
                  borderRadius="xl"
                  p={10}
                  w="full"
                  bg={uploadFile ? "green.50" : "gray.50"}
                  textAlign="center"
                  transition="all 0.2s"
                  _hover={{ borderColor: "green.500", bg: "green.50" }}
                  position="relative"
                >
                  <Input
                    type="file"
                    id="bulkFileInput"
                    height="100%"
                    width="100%"
                    opacity="0"
                    position="absolute"
                    top="0"
                    left="0"
                    cursor="pointer"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                  <VStack spacing={3}>
                    <Icon as={FiUpload} boxSize={10} color={uploadFile ? "green.600" : "gray.400"} />
                    <Heading size="md" color={uploadFile ? "green.700" : "gray.600"}>
                      {uploadFile ? "File Selected" : "Drag and drop or click to upload"}
                    </Heading>
                    <Text color="gray.500">
                      {uploadFile ? uploadFile.name : "Accepted formats: .xlsx, .xls, .csv"}
                    </Text>
                    {uploadFile && <Badge colorPalette="green" fontSize="0.9em">Ready to Upload</Badge>}
                  </VStack>
                </Box>

                {uploadMessage && (
                  <Alert.Root status={uploadMessageType === 'success' ? 'success' : 'error'} borderRadius="md">
                    <Alert.Indicator />
                    {uploadMessage}
                  </Alert.Root>
                )}

                <Button
                  size="lg"
                  colorPalette="green"
                  w="full"
                  onClick={handleUpload}
                  disabled={!uploadFile || uploading}
                  loading={uploading}
                  loadingText="Uploading..."
                >
                  <FiUpload style={{ marginRight: '8px' }} /> Upload File
                </Button>

                {/* Data Templates */}
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} w="full" mt={4}>
                  <Card.Root shadow="sm" borderRadius="lg">
                    <Card.Header pb={2}>
                      <Heading size="sm" display="flex" alignItems="center" gap={2}>
                        <Icon as={FiDownload} /> Data Templates
                      </Heading>
                    </Card.Header>
                    <Card.Body pt={0}>
                      <Text mb={4} fontSize="sm" color="gray.600">Download example files to ensure your data is formatted correctly.</Text>
                      <HStack spacing={4}>
                        <Button variant="outline" colorPalette="green" onClick={handleDownloadExampleExcel} flex={1} size="sm">
                          <FiFileText style={{ marginRight: '8px' }} /> Excel
                        </Button>
                        <Button variant="outline" colorPalette="blue" onClick={handleDownloadExampleCSV} flex={1} size="sm">
                          <FiFileText style={{ marginRight: '8px' }} /> CSV
                        </Button>
                      </HStack>
                    </Card.Body>
                  </Card.Root>

                  <Card.Root shadow="sm" borderRadius="lg">
                    <Card.Header pb={2}>
                      <Heading size="sm" display="flex" alignItems="center" gap={2}>
                        <Icon as={FiCheckCircle} /> Auto-Generated Fields
                      </Heading>
                    </Card.Header>
                    <Card.Body pt={0}>
                      <VStack spacing={2} align="stretch">
                        <HStack>
                          <Icon as={FiCheckCircle} color="green.500" />
                          <Text fontSize="sm"><strong>DATE_UPLOAD</strong> - Auto set to current time</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FiCheckCircle} color="green.500" />
                          <Text fontSize="sm"><strong>UPLOADER</strong> - Auto set to your email</Text>
                        </HStack>
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </SimpleGrid>
              </VStack>
            </Card.Body>
          </Card.Root>

        </VStack>
      </Container>

      {/* Loading Overlay Modal */}
      <Dialog.Root open={isSubmitting} closeOnInteractOutside={false} closeOnEscape={false}>
        <Dialog.Backdrop bg="blackAlpha.700" />
        <Dialog.Positioner>
          <Dialog.Content bg="white" borderRadius="xl" p={8} maxW="sm" textAlign="center">
            <VStack spacing={6}>
              <Spinner size="xl" color="purple.500" borderWidth="4px" />
              <Heading size="lg" color="purple.600">ĐANG TẠO AGENT</Heading>
              <Text color="gray.600">Đang xử lý yêu cầu của bạn...</Text>
              <Text fontSize="sm" color="gray.400">Vui lòng chờ trong giây lát...</Text>
            </VStack>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

export default CreateAgent;
